<template>
  <div>
    <div class="row q-col-gutter-md q-mb-md">
      <div class="col col-grow" v-if="pdfDocumentLink">
        <QBtn
          class="q-pa-xs full-width"
          outline
          type="a"
          :ripple="false"
          color="grey-9"
          label="PDF"
          :href="pdfDocumentLink"
        />
      </div>
      <div class="col col-grow" v-if="!isDisabled">
        <QBtn
          v-if="!erpquote.price.onRoadCost"
          class="q-pa-xs full-width"
          color="grey-9"
          unelevated
          :disable="isAwaitingPrice || requestingPricing"
          :ripple="false"
          :label="
            isAwaitingPrice || requestingPricing
              ? 'Awaiting Quote...'
              : 'Request Quote'
          "
          @click="requestPricing"
        />
        <QBtn
          v-else
          class="q-pa-xs full-width"
          color="grey-9"
          unelevated
          :disable="isSubmitting || requestingSignature"
          :ripple="false"
          :label="saveAndSendLabel"
          @click="saveAndSend"
        />
      </div>
    </div>
    <p class="text-grey-9" v-if="!isDisabled">{{ caption }}</p>
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex';
import { QBtn } from 'eclipx.ui';
import MutateRequestSignature from '../../graphql/mutations/MutateRequestSignature';
import MutateRequestPricing from '../../graphql/mutations/MutateRequestPricing';
import { GLOBAL_ERROR } from '../GlobalError/GlobalError';

/**
 * This component represents actions that can be taken against a quote at different stages of its completion.
 */
export default {
  name: 'QuoteReviewActions',
  data() {
    return {
      requestingPricing: false,
      requestingSignature: false
    };
  },
  computed: {
    ...mapState({
      dbquote: (state) => state.quote.quote.dbquote,
      erpquote: (state) => state.quote.quote.erpquote
    }),
    ...mapGetters({
      isAwaitingPrice: 'quote/isAwaitingPrice',
      isSubmitting: 'quote/isSubmitting',
      isAccepted: 'quote/isAccepted',
      isExpired: 'quote/isExpired'
    }),
    isDisabled: function() {
      return this.isAccepted || this.isExpired;
    },
    caption: function() {
      if (!this.erpquote.price.onRoadCost) {
        return this.isAwaitingPrice
          ? 'The price will automatically appear above once dealership have uploaded a quote'
          : 'Request a quote from your dealers';
      }

      if (this.isSubmitting) {
        return 'Quote generated and awaiting customer response';
      }

      return null;
    },
    pdfDocumentLink: function() {
      if (!this.isAccepted || !process.env.VUE_APP_PDF_DOCUMENT) {
        return null;
      }

      return `${process.env.VUE_APP_PDF_DOCUMENT}${this.dbquote.docusign_ref}`;
    },
    saveAndSendLabel: function() {
      if (this.requestingSignature) {
        return 'Sending Quote...';
      }

      if (this.isSubmitting) {
        return 'Awaiting Approval...';
      }

      return 'Save & Send';
    },
    docusignBlurb: function() {
      return `Dear ${this.erpquote.customer.customerName}, thank you for requesting this quote no. ${this.erpquote.erpQuoteRef}. By completing this form our team will be in contact with you shortly to answer any questions you may have and explain the next steps toward getting your new vehicle.`;
    },
    docusignSubject: function() {
      return `FME Lease Quote Estimate ${this.erpquote.erpQuoteRef}`;
    }
  },
  methods: {
    ...mapMutations({ setDbQuoteFields: 'quote/setDbQuoteFields' }),
    requestPricing: async function() {
      this.requestingPricing = true;
      return await this.$apollo
        .mutate({
          mutation: MutateRequestPricing,
          variables: {
            params: {
              dbQuoteId: this.dbquote.id,
              createdBy: this.$store.getters['auth/user'].email
            }
          },
          update: (store, response) => {
            if (response.data.requestPricing) {
              let code =
                response.data.requestPricing.dealerPortal.quote_status_code;
              this.setDbQuoteFields({ quote_status_code: code });
            }
          }
        })
        .catch(() => {
          this.$euiNotifications.emit({
            notification: GLOBAL_ERROR,
            action: () => {
              return this.requestPricing();
            }
          });
        })
        .finally(() => {
          this.requestingPricing = false;
        });
    },
    saveAndSend: async function() {
      this.requestingSignature = true;
      return await this.$apollo
        .mutate({
          mutation: MutateRequestSignature,
          variables: {
            params: {
              dbQuoteId: this.dbquote.id,
              orgId: this.$store.getters['auth/user'].organisation.id,
              erpQuoteRef: this.erpquote.erpQuoteRef,
              emailSubject: this.docusignSubject,
              emailBlurb: this.docusignBlurb,
              customerName: this.erpquote.customer.customerName,
              customerEmail: this.erpquote.customer.customerContactEmail
            }
          },
          update: (store, response) => {
            if (response.data.requestSignature) {
              this.setDbQuoteFields(response.data.requestSignature.quote);
            }
          }
        })
        .catch(() => {
          this.$euiNotifications.emit({
            notification: GLOBAL_ERROR,
            action: () => {
              return this.saveAndSend();
            }
          });
        })
        .finally(() => {
          this.requestingSignature = false;
        });
    }
  },
  components: {
    QBtn
  }
};
</script>

<style lang="sass" scoped></style>
